import { transformBenefitsList } from "@/app/(site)/_utils/transformData/transformBenfitList"
import {
    type ProductData,
    type RelatedVariant
} from "@/lib/content-service/types/ContentServiceProduct"
import placeholder from "@assets/images/placeholder.png"
import React from "react"

type CurrentProduct = {
    [key: string]: any
}

/**
 * A helper function to manage state and derive additional product data for presentation in a UI component.
 * @param product - The product object derived from the ProductData type.
 * @param override - An optional parameter for overriding specific product attributes.
 * @returns An object containing several pieces of derived product data, along with the current product state and a function to update it.
 */
export const useProductDisplayInfo = (products: ProductData[], override?: any) => {
    const [currentProduct, setCurrentVariant] = React.useState<CurrentProduct>({
        media: products?.[0]?.media ?? [],
        name: products?.[0]?.name ?? "",
        price: products?.[0]?.price ?? "",
        priceBeforeDiscount: products?.[0]?.priceBeforeDiscount ?? "",
        available: products?.[0]?.available ?? false,
        uri: products?.[0]?.uri ?? "",
        showAsOnSale: products?.[0]?.showAsOnSale ?? "",
        color_swatch: products?.[0]?.color_swatch ?? "",
        showAsNew: products?.[0]?.showAsNew ?? "",
        priceAsNumber: products?.[0]?.priceAsNumber ?? "",
        benefitList: products?.[0]?.benefitList,
        badges: products?.[0]?.combinedBadges
    })

    React.useEffect(() => {
        setCurrentVariant({
            media: products?.[0]?.media ?? [],
            name: products?.[0]?.name ?? "",
            price: products?.[0]?.price ?? "",
            priceBeforeDiscount: products?.[0]?.priceBeforeDiscount ?? "",
            available: products?.[0]?.available ?? false,
            uri: products?.[0]?.uri ?? "",
            showAsOnSale: products?.[0]?.showAsOnSale ?? "",
            color_swatch: products?.[0]?.color_swatch ?? "",
            showAsNew: products?.[0]?.showAsNew ?? "",
            priceAsNumber: products?.[0]?.priceAsNumber ?? "",
            benefitList: products?.[0]?.benefitList,
            badges: products?.[0]?.combinedBadges
        })
    }, [products])

    const primary = products?.[0]
    const variants: Array<ProductData | RelatedVariant> =
        primary && primary.relatedVariants && primary.relatedVariants.length > 0
            ? [primary, ...primary.relatedVariants]
            : (products ?? [])

    const uri = "/product/" + (products?.[0]?.uri ?? "")
    const badges = products?.[0]?.combinedBadges
    const colorVariantsAmount = variants.length

    const {
        name,
        available,
        priceBeforeDiscount,
        price,
        showAsOnSale,
        showAsNew,
        benefitList,
        media
    } = currentProduct

    const hasMedia = Array.isArray(media) && media.length > 0
    const initialImageSrc = hasMedia ? media[0]?.url : placeholder
    const initialHoverImageSrc =
        hasMedia && media.length > 1 ? media[1]?.url : media[0]?.url

    const benefits = transformBenefitsList(
        Array.isArray(benefitList) ? benefitList[0] : benefitList
    ).splice(0, 3)

    const colorSwatches = variants
        .map(product => ({
            product: product as ProductData,
            name: product?.name,
            color: product?.color_swatch?.hex,
            colorName: product?.color_swatch?.color_name,
            // ProductData does not carry a top-level sort order field;
            // variants sort by URI as fallback
            sortOrder: NaN,
            uri: product?.uri,
            priceBeforeDiscount: product?.priceBeforeDiscount,
            lens: product?.lens,
            media: product?.media,
            price: product?.price,
            available: product?.available,
            showAsNew: product?.showAsNew,
            showAsOnSale: product?.showAsOnSale,
            image: product?.lens?.image,
            badges: product?.combinedBadges
        }))
        .filter(swatch => swatch.color)

    let sortedColor = colorSwatches.sort((a, b) => {
        const orderA = a.sortOrder || Infinity
        const orderB = b.sortOrder || Infinity
        return orderA - orderB || (a?.uri || "").localeCompare(b?.uri || "")
    })

    const hoverImage = initialHoverImageSrc ?? placeholder
    const initialImageMaybeWithOverride = override?.image?.url || initialImageSrc
    const productImageAlt = override?.image?.alt || name
    const extraColorsCount = sortedColor.length > 6 ? sortedColor.length - 6 : 0

    sortedColor = sortedColor.slice(0, 6)

    const anyVariantisAvailable = isProductAvailability(variants as ProductData[])

    return {
        currentProduct,
        setCurrentVariant,
        uri,
        badges,
        colorVariantsAmount,
        benefits,
        sortedColor,
        hoverImage,
        initialImageMaybeWithOverride,
        available,
        anyVariantisAvailable,
        showAsNew,
        extraColorsCount,
        name,
        priceBeforeDiscount,
        price,
        showAsOnSale,
        productImageAlt
    }
}

export function isProductAvailability(products: ProductData[]): boolean {
    if (!Array.isArray(products) || products.length < 1) {
        return false
    }

    const isAvailable = products.some(item => item.available)
    return isAvailable
}
